import React, { useState, useRef, useEffect } from 'react';
import Icon from '../primitives/Icon';
import { Button } from '../../components/ui/button';
import { cn } from '../../components/ui/cn';
import { formatMetricNumber, metricMeta } from '../chat-blocks/metricTone';
import './chat.css';

function PhaseDetail({ phase, detail }) {
  if (!detail || !phase) return null;
  const items = [];

  if (phase === '2_2_preprocess') {
    const merged = detail.merged_datasets || [];
    if (merged.length > 0) items.push(`Merged: ${merged.join(' + ')}`);
    if (detail.merge_key) items.push(`Join key: ${detail.merge_key}`);
  } else if (phase === '2_3_clean') {
    if (detail.dropped_columns?.length)
      items.push(`Dropped: ${detail.dropped_columns.join(', ')}`);
    if (detail.capped_columns?.length)
      items.push(`Capped cardinality: ${detail.capped_columns.join(', ')}`);
  } else if (phase === '3_model') {
    if (detail.test_metric != null && detail.metric_name) {
      const { label } = metricMeta(detail.metric_name);
      items.push(
        `${label.charAt(0).toUpperCase()}${label.slice(1)}: `
        + `${formatMetricNumber(detail.test_metric, detail.metric_name)}`,
      );
    }
    if (detail.mode_collapse) items.push('⚠ Mode collapse detected');
  } else if (phase === '5_explain') {
    if (detail.top_features?.length)
      items.push(`Top features: ${detail.top_features.slice(0, 3).join(', ')}`);
  }

  if (items.length === 0) return null;
  return (
    <ul className="q-steps__detail">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

function StepHistoryPopover({ history, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div ref={ref} className="q-steps">
      {history.length === 0 && (
        <div className="q-steps__empty">No completed steps yet</div>
      )}
      {history.map((entry, i) => (
        <StepRow key={entry.phase || i} entry={entry} />
      ))}
    </div>
  );
}

function StepRow({ entry }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = entry.detail && Object.keys(entry.detail).some(k => {
    const v = entry.detail[k];
    return Array.isArray(v) ? v.length > 0 : v != null && v !== '';
  });

  return (
    <div className="q-steps__row">
      <div
        className={cn('q-steps__line', hasDetail && 'q-steps__line--open')}
        onClick={() => hasDetail && setExpanded(e => !e)}
      >
        <Icon name="check" size={13} />
        <span className="q-steps__label">{entry.label}</span>
        {entry.timestamp && (
          <span className="q-steps__time">
            {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
        {hasDetail && (
          <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={12} />
        )}
      </div>
      {expanded && hasDetail && (
        <PhaseDetail phase={entry.phase} detail={entry.detail} />
      )}
    </div>
  );
}

const AgentStatusBar = ({ phase, stepHistory = [], agentStopped = false, onStop, onResume }) => {
  const [popoverOpen, setPopoverOpen] = useState(false);

  const pillLabel = agentStopped ? 'Paused' : (phase || 'Thinking');

  return (
    <div className="q-status">
      <Button
        variant="outline"
        size="sm"
        className={cn(agentStopped && 'q-status__phase--paused')}
        onClick={() => setPopoverOpen(o => !o)}
        aria-expanded={popoverOpen}
      >
        <span>{pillLabel}</span>
        {stepHistory.length > 0 && (
          <span className="q-status__count">{stepHistory.length}</span>
        )}
      </Button>

      {agentStopped ? (
        <Button variant="outline" size="sm" onClick={onResume} title="Resume">
          <Icon name="play" data-icon="inline-start" />
          Resume
        </Button>
      ) : (
        <Button variant="outline" size="sm" onClick={onStop} title="Stop">
          <Icon name="square" data-icon="inline-start" />
          Stop
        </Button>
      )}

      {popoverOpen && (
        <StepHistoryPopover
          history={stepHistory}
          onClose={() => setPopoverOpen(false)}
        />
      )}
    </div>
  );
};

export default AgentStatusBar;
